import { Controller } from "@hotwired/stimulus"

// Accessible tabs for the watch player's supporting content. The controller is
// intentionally data-only: richer recommendation metadata can change the panel
// contents later without changing how the player switches between them.
export default class extends Controller {
  static targets = ["tab", "panel", "relatedCount", "relatedContent"]
  static values = { relatedUrl: String, relatedLessonId: String }

  connect() {
    this.desiredRelatedLessonId = this.hasRelatedLessonIdValue ? this.relatedLessonIdValue : null
    this.loadedRelatedContextKey = null
    this.pendingRelatedContextKey = null
    this.show(this.selectedTabName() || this.tabTargets[0]?.dataset.playerTab)
    this.loadRelated()
  }

  disconnect() {
    this.relatedRequest?.abort()
  }

  select(event) {
    this.show(event.currentTarget.dataset.playerTab)
  }

  openFromEvent(event) {
    this.show(event.detail?.tab)
    this.tabTargets.find((tab) => tab.dataset.playerTab === event.detail?.tab)?.focus()
  }

  refreshRelated(event) {
    const lessonId = event.detail?.lessonId
    if (!lessonId) return

    this.desiredRelatedLessonId = String(lessonId)
    if (this.selectedTabName() === 'related') this.loadRelated()
  }

  retryRelated() {
    this.loadedRelatedContextKey = null
    this.loadRelated()
  }

  async loadRelated() {
    if (!this.hasRelatedUrlValue || !this.hasRelatedContentTarget) return

    const lessonId = this.desiredRelatedLessonId
    const contextKey = lessonId || 'whole-video'
    if (contextKey === this.loadedRelatedContextKey || contextKey === this.pendingRelatedContextKey) return

    this.pendingRelatedContextKey = contextKey

    this.relatedRequest?.abort()
    const request = new AbortController()
    this.relatedRequest = request
    const url = new URL(this.relatedUrlValue, window.location.origin)
    if (lessonId) url.searchParams.set('video_lesson_id', lessonId)

    this.relatedContentTarget.setAttribute('aria-busy', 'true')
    try {
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: request.signal
      })
      if (!response.ok) throw new Error(`Related videos request failed: ${response.status}`)

      const template = document.createElement('template')
      template.innerHTML = (await response.text()).trim()
      const content = template.content.firstElementChild
      if (!content) return

      const count = Number.parseInt(content.dataset.relatedCount || '0', 10)
      this.relatedContentTarget.replaceChildren(content)
      this.loadedRelatedContextKey = contextKey
      if (this.hasRelatedCountTarget) {
        this.relatedCountTarget.textContent = ` · ${count}`
        this.relatedCountTarget.hidden = count === 0
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Could not refresh related videos:', error)
        this.showRelatedError()
      }
    } finally {
      if (this.relatedRequest === request) {
        this.pendingRelatedContextKey = null
        this.relatedContentTarget.removeAttribute('aria-busy')
      }
    }
  }

  showRelatedError() {
    const message = document.createElement('p')
    message.className = 'player-empty-state'
    message.textContent = 'Related lessons could not be loaded.'
    const retry = document.createElement('button')
    retry.type = 'button'
    retry.className = 'player-related-retry'
    retry.dataset.action = 'player-tabs#retryRelated'
    retry.textContent = 'Try again'
    const wrapper = document.createElement('div')
    wrapper.className = 'player-related-error'
    wrapper.append(message, retry)
    this.relatedContentTarget.replaceChildren(wrapper)
  }

  keydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return

    event.preventDefault()
    const currentIndex = this.tabTargets.indexOf(event.currentTarget)
    let nextIndex

    if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = this.tabTargets.length - 1
    else if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabTargets.length
    else nextIndex = (currentIndex - 1 + this.tabTargets.length) % this.tabTargets.length

    const nextTab = this.tabTargets[nextIndex]
    this.show(nextTab.dataset.playerTab)
    nextTab.focus()
  }

  show(name) {
    if (!name) return

    this.tabTargets.forEach((tab) => {
      const selected = tab.dataset.playerTab === name
      tab.setAttribute('aria-selected', selected.toString())
      tab.tabIndex = selected ? 0 : -1
    })

    this.panelTargets.forEach((panel) => {
      panel.hidden = panel.dataset.playerPanel !== name
    })

    if (name === 'related') this.loadRelated()
  }

  selectedTabName() {
    return this.tabTargets.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.playerTab
  }
}
